import { useEffect, useMemo, useSyncExternalStore } from "react";
import { useNumberFormattingLocale } from "~/lib/language-lens";
import {
  type DisplayCurrency,
  getDenominationCurrency,
  subscribeToDenominationLens,
} from "./denomination-lens";
import {
  type FxRate,
  getFxRate,
  requestFxRateHydration,
  subscribeToFxRateChanges,
} from "./fx-rate";
import {
  createMarketValueFormatter,
  type MarketValueFormatter,
} from "./market-value-formatter";

function getServerDenominationLensSnapshot(): "USD" {
  return "USD";
}

export interface DenominationFormatContext {
  readonly currency: Exclude<DisplayCurrency, "USD">;
  readonly rate: number;
}

function createDenominationFormatContext(
  currency: DisplayCurrency,
  fxRate: FxRate | null,
): DenominationFormatContext | null {
  if (currency === "USD" || fxRate === null) {
    return null;
  }

  return {
    currency,
    rate: fxRate.rate,
  };
}

/**
 * Imperative denomination snapshot for non-reactive formatting helpers.
 * It reflects the lens state at call time and does not subscribe callers to
 * later lens or FX changes.
 */
export function getDenominationFormatContext(): DenominationFormatContext | null {
  const currency = getDenominationCurrency();
  return createDenominationFormatContext(
    currency,
    currency === "USD" ? null : getFxRate("USD", currency),
  );
}

export function useFxRateDisplay(quote: DisplayCurrency): FxRate | null {
  const fxRate = useSyncExternalStore(
    subscribeToFxRateChanges,
    () => (quote === "USD" ? null : getFxRate("USD", quote)),
    () => null,
  );

  useEffect(() => {
    if (quote !== "USD") {
      requestFxRateHydration("USD", quote);
    }
  }, [quote]);

  return fxRate;
}

/**
 * Render-pure denomination snapshot for animation-sensitive formatting.
 * Both stores use referentially stable `useSyncExternalStore` snapshots and
 * FX hydration is requested only after commit.
 */
export function useDenominationFormatContext(): DenominationFormatContext | null {
  const currency = useSyncExternalStore(
    subscribeToDenominationLens,
    getDenominationCurrency,
    getServerDenominationLensSnapshot,
  );
  const fxRate = useFxRateDisplay(currency);

  return useMemo(
    () => createDenominationFormatContext(currency, fxRate),
    [currency, fxRate],
  );
}

/**
 * Subscribed formatter for public market values. It deliberately uses only
 * referentially-stable external-store snapshots plus effect-based hydration;
 * query hooks here would add settling renders to animation-sensitive callers.
 */
export function useMarketValueFormatter(): MarketValueFormatter {
  const denomination = useDenominationFormatContext();
  const numberFormattingLocale = useNumberFormattingLocale();

  return useMemo(() => {
    if (denomination === null) {
      return createMarketValueFormatter({});
    }

    return createMarketValueFormatter({ denomination });
  }, [denomination, numberFormattingLocale]);
}
